import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { Td } from "../../features/web3/forex/components/table";
import { formatDateTime } from "../../lib/format";
import { useCan, useForexAdmin } from "./api";
import { RecordHistory } from "./Audit";
import { BASE, Fig, ListView, Money, Panel, PeriodLink, QueryState, Stat, Unavail, UserLink, linkCls, mono, muted } from "./parts";
import { AccessFee, HiddenNote, HideToggle, MasterType, PROVIDER, ProviderCell, SharesPositions, StoppedNote, SwitchProvider } from "./rows";

// The master's cover (v1.1.0): Ginox's own account carrying this master's
// exposure to the provider, never the user's. login/group are Ginox's records;
// the figures come from the mirror and are null while it is down.
function Cover({ cover }) {
  if (!cover) return <p className="text-sm text-muted-foreground">No cover: this master was made before covers.</p>;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {/* Not linked: covers never show on the account routes (013). */}
      <Stat label="Cover login">{cover.login}</Stat>
      <Stat label="Group"><span className="break-all text-xs">{cover.group}</span></Stat>
      <Stat label="Balance"><Fig v={cover.balance} /></Stat>
      <Stat label="Equity"><Fig v={cover.equity} /></Stat>
      <Stat label="Margin"><Fig v={cover.margin} /></Stat>
      <Stat label="Free margin"><Fig v={cover.freeMargin} /></Stat>
      {/* 012: null margin = mirror down; null level with margin = none in use; a real "0" is an alarm. */}
      <Stat label="Margin level" tone={Number(cover.marginLevel) === 0 && cover.marginLevel != null ? "text-red-400" : undefined}>
        {cover.margin == null ? <Unavail /> : cover.marginLevel == null ? "—" : `${cover.marginLevel}%`}
      </Stat>
    </div>
  );
}

const PERIOD_COLUMNS = [
  { label: "Starts" }, { label: "Ends" }, { label: "Cut", align: "right" }, { label: "Schedule" },
  { label: "Status" }, { label: "Recorded" }, { label: "Settlements", align: "right" }, { label: "Paid", align: "right" },
  { label: "Carried", align: "right" }, { label: "Earned", align: "right" },
];

const periodRow = (r) => (
  <tr key={r.id} className="hover:bg-white/[0.02]">
    <Td className={muted}>{formatDateTime(r.startsAt)}</Td>
    <Td className={muted}>{formatDateTime(r.endsAt)}</Td>
    <Td align="right" className={mono}>{r.cutPercent}%</Td>
    <Td className="whitespace-nowrap text-xs">{r.schedule.replaceAll("_", " ")}</Td>
    <Td className="whitespace-nowrap text-xs">{r.closedAt ? `Closed ${formatDateTime(r.closedAt)}` : <span className="text-emerald-400">Running</span>}</Td>
    {/* Null until every copier with a window on the period has a settlement. */}
    <Td className={muted}>{r.recordedAt ? formatDateTime(r.recordedAt) : "—"}</Td>
    <Td align="right" className={mono}>
      <Link to={`${BASE}/settlements?tab=settlements&period=${r.id}`} className={linkCls}>{r.settlements}</Link>
    </Td>
    <Td align="right" className={mono}>{r.paidSettlements}</Td>
    <Td align="right" className={mono}>{r.carriedSettlements}</Td>
    <Td align="right"><Money value={r.earned} /></Td>
  </tr>
);

// Performance reads only the mirror, so it is its own call: a 503 here never
// blanks the master's stored record above it.
function Performance({ id }) {
  const q = useForexAdmin(`/masters/admin/${id}/performance`);
  return (
    <QueryState q={q}>
      {(p) => (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Since becoming a master: {formatDateTime(p.from)} → {formatDateTime(p.to)}. Trade deals only; copier charges never count.
          </p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Stat label="Closed profit"><Money value={p.closedProfit} /></Stat>
            <Stat label="Commission"><Money value={p.commission} /></Stat>
            <Stat label="Trades">{p.trades}</Stat>
            <Stat label="Winning">{p.winningTrades}</Stat>
            <Stat label="Win rate">{p.trades ? `${Math.round((p.winningTrades / p.trades) * 100)}%` : "—"}</Stat>
            <Stat label="Balance"><Fig v={p.balance} /></Stat>
            <Stat label="Equity"><Fig v={p.equity} /></Stat>
            <Stat label="Margin"><Fig v={p.margin} /></Stat>
            <Stat label="Free margin"><Fig v={p.freeMargin} /></Stat>
            <Stat label="Floating"><Fig v={p.floatingProfit} /></Stat>
          </div>
        </div>
      )}
    </QueryState>
  );
}

export default function MasterDetail() {
  const { id } = useParams();
  // v1.23.0: a provider switch runs in the background, so read the master again every
  // few seconds while one is set, up to about two minutes from its start (037). Past
  // that, or stuck, Refresh shows where it is.
  const q = useForexAdmin(`/masters/admin/${id}`, undefined, {
    refetchInterval: (query) => {
      const s = query.state.data?.providerSwitch;
      return s && Date.now() - Date.parse(s.startedAt) < 120_000 ? 5000 : false;
    },
  });
  const can = useCan();
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;
  const m = q.data;

  // The switch this page started, and what came of it. Done and undone both end with
  // providerSwitch null; only the provider tells them apart.
  const [asked, setAsked] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const switching = !!m?.providerSwitch;
  const wasSwitching = useRef(switching);
  useEffect(() => {
    if (wasSwitching.current && !switching && m) {
      qc.invalidateQueries({ queryKey: ["forex-admin"] });
      if (asked) {
        setOutcome(m.provider === asked ? `Switched to ${PROVIDER[asked]}.` : "Switch undone. See History for the reason.");
        setAsked(null);
      }
    }
    wasSwitching.current = switching;
  }, [switching, m, asked, qc]);

  return (
    <QueueLayout
      eyebrow="Forex Admin · Master"
      title={m ? m.name ?? "Unnamed master" : "Master"}
      description={m ? `${m.cutPercent}% cut · ${m.schedule.replaceAll("_", " ")}${m.sponsorPercent > 0 ? ` · sponsor ${m.sponsorPercent}%` : ""} · min equity ${m.minimumEquity} · leverage ${m.leverage == null ? "—" : `1:${m.leverage}`}` : null}
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      <Link to={`${BASE}/masters`} className={`text-xs text-muted-foreground ${linkCls}`}>← All masters</Link>

      <QueryState q={q}>
        {(m) => (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Type"><MasterType type={m.type} /></Stat>
              <Stat label="Status" tone={m.stoppedAt ? "text-red-400" : undefined}>
                {m.stoppedAt ? "Stopped" : "Standing"}
                <StoppedNote m={m} />
              </Stat>
              {/* Null once the master's account was removed. */}
              <Stat label="Tier">{m.tier ?? "—"}</Stat>
              <Stat label="Master list" tone={m.hidden ? "text-amber-400" : undefined}>
                {m.hidden ? "Hidden" : "Shown"}
                <HiddenNote m={m} />
                <div className="mt-2"><HideToggle m={m} /></div>
              </Stat>
              <Stat label="Disconnecting copiers" tone={m.disconnectingCopiers ? "text-amber-400" : undefined}>
                <span className="text-sm">
                  {m.disconnectingCopiers ? "Disconnecting all. Nobody can connect until it ends." : "No"}
                </span>
              </Stat>
              <Stat label="Shares positions"><SharesPositions v={m.sharesPositions} /></Stat>
              <Stat label="Access fee"><AccessFee v={m.accessFee} /></Stat>
              {/* v1.25.0: out of each share, for copiers its BD referred. Earned below is what it kept. */}
              <Stat label="Sponsor share">{m.sponsorPercent}%</Stat>
              <Stat label="Provider">
                <ProviderCell m={m} />
                {outcome && !switching && <span className="block text-xs text-muted-foreground">{outcome}</span>}
                <div><SwitchProvider m={m} onStarted={(t) => { setAsked(t); setOutcome(null); }} /></div>
              </Stat>
              <Stat label="Owner"><UserLink username={m.username} /></Stat>
              <Stat label="Login">
                {/* A stopped master's account is gone; its kept login opens Deleted Accounts (028). */}
                {m.login ? (
                  <Link to={m.stoppedAt ? `${BASE}/deleted-accounts/${m.login}` : `${BASE}/accounts?login=${m.login}`} className={linkCls}>{m.login}</Link>
                ) : "Removed"}
              </Stat>
              <Stat label="Copiers">
                <Link to={`${BASE}/connections?master=${m.id}`} className={linkCls}>{m.copierCount}</Link>
              </Stat>
              <Stat label="Earned (lifetime)"><Money value={m.earned} /></Stat>
              <Stat label="Earned this period"><Money value={m.periodEarned} /></Stat>
              {/* Owed but not moved yet. Never add it to the running share. */}
              <Stat label="Waiting to land">
                <Money value={m.waitingToLand} />
                {/* v1.28.0 (042): of it, what the copiers paid, by release time. A past one is being retried. */}
                {(m.awaitingRelease ?? []).map((r) => (
                  <span key={r.releaseAt} className="block text-xs text-muted-foreground">
                    <Money value={r.amount} /> · {Date.parse(r.releaseAt) <= Date.now() ? "being paid" : `on ${formatDateTime(r.releaseAt)}`}
                  </span>
                ))}
              </Stat>
              <Stat label="Running share (estimate)"><Fig v={m.runningShare} /></Stat>
              <Stat label="Current period">
                <span className="text-xs">
                  {m.period ? `${formatDateTime(m.period.startsAt)} → ${formatDateTime(m.period.endsAt)} at ${m.period.cutPercent}%, ${m.period.schedule.replaceAll("_", " ")}` : m.stoppedAt ? "Stopped" : "Between periods"}
                </span>
                {m.period && <span className="block"><PeriodLink id={m.period.id} /></span>}
              </Stat>
            </div>
            <p className="text-xs text-muted-foreground">
              Created {formatDateTime(m.createdAt)}
              {m.termsChangedAt && ` · terms changed ${formatDateTime(m.termsChangedAt)}`}
            </p>
            <Panel title="Cover">
              <Cover cover={m.cover} />
            </Panel>
          </div>
        )}
      </QueryState>

      {/* A stopped master's account is gone, and performance answers 404 for it. */}
      <Panel title="Performance">
        {m?.stoppedAt ? (
          <p className="text-xs text-muted-foreground">Account removed, no live figures.</p>
        ) : m ? (
          <Performance id={id} />
        ) : null}
      </Panel>

      <Panel title="Periods">
        <ListView path={`/masters/admin/${id}/periods`} empty={{}} fields={[]}
          columns={PERIOD_COLUMNS} row={periodRow} label="periods" />
      </Panel>

      {/* Who opened, hid or unhid it (026). */}
      {can("audit.read") && (
        <Panel title="History">
          <RecordHistory type="master" id={id} />
        </Panel>
      )}
    </QueueLayout>
  );
}
